import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { ToiDetalhe, ToiMidiaItem } from '../models/toi-detalhe.model';
import { PdfToiResponse } from '../models/pdf-toi.model';
import { ToiExportPdfService } from '../toi-export-pdf.service';
import {
  checklistValorPorLabel,
  checklistMarcada as checklistMarcadaNoChecklist,
  formatarEconomiaColchete,
  irregularidadeDeveMarcar,
  marcarCheckbox,
  mascararNomePdf,
  resolverDescricaoInfracaoToi,
  resolverParentescoChecklistToi,
  marcarLocalizacaoToi,
  PdfToiEconomiaCategoriaCampo,
  resolverChecklistAssinaturaSrc,
  somarEconomiaPorCategoria,
  temChecklistAssinatura as temChecklistAssinaturaValor,
} from '../utils/toi-export-pdf.util';

@Component({
  selector: 'mf-toi-export-pdf',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './toi-export-pdf.component.html',
  styleUrl: './toi-export-pdf.component.scss',
})
export class ToiExportPdfComponent {
  /** Máximo de fotos por página PDF (2 colunas × 5 linhas) — evita corte após a 10ª foto. */
  readonly fotosPorPagina = 6;
  readonly colunasFotos = 2;
  readonly linhasFotos = 3;
  /** URL absoluta — necessária para o html2canvas na janela de impressão (about:blank). */
  readonly informativoToiSrc = new URL(
    'assets/pdf-toi-copasa/Informativo_TOI_Modelo2.png',
    document.baseURI
  ).href;

  @Input() pdfToi: PdfToiResponse | null = null;
  @Input() detalhe: ToiDetalhe | null = null;
  @Input() midiasOcultas: Set<string> = new Set();

  readonly marcarCheckbox = marcarCheckbox;
  readonly mascararNomePdf = mascararNomePdf;

  constructor(private readonly toiExportPdfService: ToiExportPdfService) {}

  get midiasVisiveis(): ToiMidiaItem[] {
    if (!this.detalhe) {
      return [];
    }
    return this.toiExportPdfService.filterMidiasVisiveis(this.detalhe, this.midiasOcultas);
  }

  get paginasFotos(): ToiMidiaItem[][] {
    const paginas: ToiMidiaItem[][] = [];
    const midias = this.midiasVisiveis;
    for (let i = 0; i < midias.length; i += this.fotosPorPagina) {
      paginas.push(midias.slice(i, i + this.fotosPorPagina));
    }
    return paginas;
  }

  get totalPaginas(): number {
    const paginaInformativo = this.tpToi === 'MODELO_2' ? 1 : 0;
    return 1 + this.paginasFotos.length + paginaInformativo;;
  }

  get dataVistoria(): string | null {
    const hst = this.pdfToi?.dadosHst;
    const data = hst?.dataVistoria || hst?.dataVistoria;
    return data?.trim() ? data : null;
  }

  get tpToi(): string {
    return this.pdfToi?.dadosHst?.tpToi ?? this.detalhe?.tpToi ?? '';
  }

  /** Número do modelo para o rodapé do termo — fallback no modelo 1, como em `resolveTpToi`. */
  get numeroModeloToi(): string {
    return this.tpToi === 'MODELO_2' ? '2' : '1';
  }

  get prazoDias(): number | null {
    return this.pdfToi?.dadosHst?.prazoDias ?? null;
  }

  get colunasIndices(): number[] {
    return Array.from({ length: this.colunasFotos }, (_, i) => i);
  }

  fotoMaxHeightPx(qtdFotosNaPagina: number): number {
    const alturaUtil = 1122 - 110 - 28;
    const alturaLegendaPadding = 28;
    const linhas = Math.ceil(qtdFotosNaPagina / this.colunasFotos);
    const porLinha = Math.floor(alturaUtil / linhas) - alturaLegendaPadding;
    return Math.max(70, porLinha);
    // 3 linhas → ~294px/foto | 2 linhas → ~452px/foto | 1 linha → ~926px/foto
  }

  economiaColchete(campo: PdfToiEconomiaCategoriaCampo): string {
    const total = somarEconomiaPorCategoria(this.pdfToi?.dadosHst?.economiaPorCategorias, campo);
    return formatarEconomiaColchete(total);
  }

  checklistValor(label: string): string {
    return checklistValorPorLabel(this.pdfToi?.checklistToi, label);
  }

  /** "[ * ] se não for o usuário, informar parentesco/afinidade com o mesmo". */
  parentescoValor(): string {
    const checklist = this.pdfToi?.checklistToi?.length
      ? this.pdfToi.checklistToi
      : this.detalhe?.checklistToi;
    return resolverParentescoChecklistToi(checklist);
  }

  checklistMarcada(label: string): boolean {
    return checklistMarcadaNoChecklist(this.pdfToi?.checklistToi, label);
  }

  marcarLocalizacao(opcao: 'Interna' | 'Externa'): boolean {
    const checklist = this.pdfToi?.checklistToi?.length
      ? this.pdfToi.checklistToi
      : this.detalhe?.checklistToi;
    return marcarLocalizacaoToi(opcao, checklist, this.detalhe?.localizacaoPadrao);
  }

  descricaoInfracaoTexto(): string {
    return resolverDescricaoInfracaoToi(
      this.pdfToi?.dadosHst?.dsTipoInfracao ?? this.detalhe?.dsTipoInfracao,
      this.pdfToi?.checklistToi ?? this.detalhe?.checklistToi,
      this.pdfToi?.irregularidadeMarcar ?? this.detalhe?.irregularidadeMarcar,
      this.detalhe
    );
  }

  marcarIrregularidade(chave: string): boolean {
    return irregularidadeDeveMarcar(this.pdfToi?.irregularidadeMarcar, chave, this.detalhe ?? undefined);
  }

  checklistAssinaturaSrc(label: string): string | null {
    return resolverChecklistAssinaturaSrc(checklistValorPorLabel(this.pdfToi?.checklistToi, label));
  }

  temChecklistAssinatura(label: string): boolean {
    return temChecklistAssinaturaValor(this.checklistValor(label));
  }
}
